import React, { useState, useEffect } from "react";
import styles from "./InvoiceViewModal.module.css";
import Button from "../ui/button";
import Input from "../ui/input";
import Dropdown from "../ui/dropdown";
import Checkbox from "../ui/checkbox";
import FormSection from "../screens/form-section";
import Table from "../ui/table";
import { MyIcons } from "../icons/my-icons";
import BudgetLineEditModal from "./budget-line-edit-modal";
import {
  getInvoiceById,
  getRectificativaById,
  getLocalities,
  getBudgetForInvoice,
  getVerifactuIncidencias,
  changeFacturaTipoCobro,
  changeRectificativaTipoCobro,
  subsanarFactura,
} from "../../services/apiService";
import { showErrorToast, showSuccessToast } from "../../utils/notifications";
import { formatDateToSpanish } from "../../utils/dateUtils";
import { isFacturaSubsanable } from "../../utils/verifactu";

/**
 * Modal para visualizar información de facturas (Factura, Pre-factura, F. Rectificativa)
 * Props:
 * - isOpen: boolean
 * - onClose: función para cerrar
 * - invoiceId: ID de la factura
 * - invoiceType: "Factura" | "Pre-factura" | "Rectificativa"
 */
export default function InvoiceViewModal({
  isOpen,
  onClose,
  invoiceId,
  invoiceType,
  subsanacionMode = false,
  onSuccess,
}) {
  const [invoiceData, setInvoiceData] = useState(null);
  const [budgetData, setBudgetData] = useState(null);
  const [loading, setLoading] = useState(false);
  const [localities, setLocalities] = useState([]);
  const [isBudgetLineModalOpen, setIsBudgetLineModalOpen] = useState(false);
  const [selectedBudgetLineId, setSelectedBudgetLineId] = useState(null);
  const [isEditingTipoCobro, setIsEditingTipoCobro] = useState(false);
  const [editedTipoCobro, setEditedTipoCobro] = useState(null);

  const [subsanacionForm, setSubsanacionForm] = useState({
    nombreFacturacion: "",
    domicilioFacturacion: "",
    localidadFacturacionId: null,
    nifFacturacion: "",
  });
  const [submittingSubsanacion, setSubmittingSubsanacion] = useState(false);

  // Cargar datos de la factura y presupuesto
  useEffect(() => {
    const loadInvoiceData = async () => {
      if (isOpen && invoiceId) {
        try {
          setLoading(true);
          // Usar el endpoint correcto según el tipo de factura
          let data = (invoiceType === "Rectificativa" || invoiceType === "F. Rectificativa")
            ? await getRectificativaById(invoiceId)
            : await getInvoiceById(invoiceId);

          if (invoiceType === "Factura" && isFacturaSubsanable(data.estadoVerifactu)
            && (!data.motivoAeat || !data.accionSiguiente)) {
            try {
              const incidencias = await getVerifactuIncidencias();
              const incidencia = incidencias.find((item) =>
                item.documentoTipo === "FACTURA" && Number(item.documentoId) === Number(invoiceId)
              );
              if (incidencia) {
                data = {
                  ...data,
                  motivoAeat: data.motivoAeat || incidencia.motivo,
                  accionSiguiente: data.accionSiguiente || incidencia.accionSiguiente,
                };
              }
            } catch (incidenciasError) {
              // Keep the invoice detail usable even if the optional incident lookup fails.
              console.error("Error loading VeriFactu incident details:", incidenciasError);
            }
          }

          setInvoiceData(data);
          setSubsanacionForm({
            nombreFacturacion: data.nombreFacturacion || "",
            domicilioFacturacion: data.domicilioFacturacion || "",
            localidadFacturacionId: data.localidadFacturacionId || null,
            nifFacturacion: data.nifFacturacion || "",
          });
          console.log("Invoice data loaded:", data);
          // Cargar datos del presupuesto si existe presupuestoId
          if (data.presupuestoId) {
            try {
              const budget = await getBudgetForInvoice(data.presupuestoId);
              console.log("Budget data loaded:", budget);
              setBudgetData(budget);
            } catch (budgetError) {
              console.error("Error loading budget data:", budgetError);
              // No mostrar error ya que es opcional
            }
          }
        } catch (error) {
          console.error("Error loading invoice:", error);
          showErrorToast("Error al cargar la factura");
        } finally {
          setLoading(false);
        }
      }
    };

    loadInvoiceData();
  }, [isOpen, invoiceId, invoiceType]);

  // Cargar localidades
  useEffect(() => {
    const loadLocalities = async () => {
      if (isOpen) {
        try {
          const localitiesData = await getLocalities();
          const formattedLocalities = (Array.isArray(localitiesData) ? localitiesData : []).map((loc) => ({
            value: loc.id,
            label: `${loc.municipio} (${loc.codigoPostal})`,
          })).filter((locality) => locality.value != null);
          setLocalities(formattedLocalities);
        } catch (error) {
          console.error("Error loading localities:", error);
        }
      }
    };

    loadLocalities();
  }, [isOpen]);

  const handleClose = () => {
    setInvoiceData(null);
    setBudgetData(null);
    setIsEditingTipoCobro(false);
    setEditedTipoCobro(null);
    setSubsanacionForm({ nombreFacturacion: "", domicilioFacturacion: "", localidadFacturacionId: null, nifFacturacion: "" });
    setSubmittingSubsanacion(false);
    onClose();
  };

  const handleEditBudgetLine = (row) => {
    console.log("Opening budget line modal for:", row);
    setSelectedBudgetLineId(row.lineaId || row.id);
    setIsBudgetLineModalOpen(true);
  };

  const handleBudgetLineModalClose = () => {
    setIsBudgetLineModalOpen(false);
    setSelectedBudgetLineId(null);
  };

  const handleStartEditTipoCobro = () => {
    setIsEditingTipoCobro(true);
    setEditedTipoCobro(selectedTipoCobro);
  };

  const handleCancelEditTipoCobro = () => {
    setIsEditingTipoCobro(false);
    setEditedTipoCobro(null);
  };

  const handleSaveTipoCobro = async () => {
    if (!editedTipoCobro) {
      showErrorToast("Seleccione un tipo de cobro");
      return;
    }

    try {
      setLoading(true);
      
      // Llamar al API correspondiente según el tipo de factura
      if (invoiceType === "Rectificativa" || invoiceType === "F. Rectificativa") {
        await changeRectificativaTipoCobro(invoiceId, editedTipoCobro.value);
      } else {
        await changeFacturaTipoCobro(invoiceId, editedTipoCobro.value);
      }

      // Actualizar los datos locales
      setInvoiceData(prev => ({ ...prev, tipoCobro: editedTipoCobro.value }));
      setIsEditingTipoCobro(false);
      showSuccessToast("Tipo de cobro actualizado correctamente");
    } catch (error) {
      console.error("Error updating tipo de cobro:", error);
      showErrorToast(error?.response?.data?.detail || error?.response?.data?.message || "Error al actualizar el tipo de cobro");
    } finally {
      setLoading(false);
    }
  };

  const handleSubmitSubsanacion = async () => {
    if (!isFacturaSubsanable(invoiceData?.estadoVerifactu)) {
      showErrorToast("Solo se pueden subsanar facturas denegadas o aceptadas con errores");
      return;
    }

    const payload = {
      nombreFacturacion: subsanacionForm.nombreFacturacion.trim(),
      domicilioFacturacion: subsanacionForm.domicilioFacturacion.trim(),
      localidadFacturacionId: subsanacionForm.localidadFacturacionId,
      nifFacturacion: subsanacionForm.nifFacturacion.trim(),
    };
    if (!payload.nombreFacturacion || !payload.domicilioFacturacion || !payload.localidadFacturacionId || !payload.nifFacturacion) {
      showErrorToast("Completa nombre, domicilio, localidad y NIF para subsanar la factura");
      return;
    }

    try {
      setSubmittingSubsanacion(true);
      const resultado = await subsanarFactura(invoiceId, payload);
      if (resultado?.estadoEnvio === "PENDIENTE_CONCILIACION") {
        showSuccessToast("Subsanación registrada; la respuesta de AEAT está pendiente de conciliación");
      } else {
        showSuccessToast(`Subsanación enviada. Estado: ${resultado?.estado || resultado?.estadoEnvio || "procesada"}`);
      }
      if (onSuccess) await onSuccess(resultado);
      handleClose();
    } catch (error) {
      console.error("Error subsanando factura:", error);
      showErrorToast(error?.response?.data?.detail || error?.response?.data?.message || "Error al enviar la subsanación");
    } finally {
      setSubmittingSubsanacion(false);
    }
  };

  if (!isOpen) return null;

  const selectedLocality = invoiceData?.localidadFacturacionId
    ? localities.find((loc) => loc.value === invoiceData.localidadFacturacionId)
    : null;

  const tipoCobroOptions = [
    { label: "Transferencia", value: "Transferencia" },
    { label: "Metalico", value: "Metalico" },
    { label: "Bizum", value: "Bizum" },
    { label: "Giro", value: "Giro" },
  ];

  const selectedTipoCobro = invoiceData?.tipoCobro
    ? tipoCobroOptions.find((opt) => opt.value === invoiceData.tipoCobro)
    : null;

  const destinatarioOptions = [
    { value: "Compañia", label: "Compañía" },
    { value: "Cliente", label: "Cliente" },
  ];

  const selectedDestinatario = invoiceData?.destinatarioFactura
    ? destinatarioOptions.find(
        (opt) => opt.value === invoiceData.destinatarioFactura
      )
    : null;

  return (
    <div className={styles.overlay} onClick={submittingSubsanacion ? undefined : handleClose}>
      <div className={styles.modal} onClick={(e) => e.stopPropagation()}>
        {/* Header */}
        <div className={styles.header}>
          <div className={styles.headerContent}>
            <div>
              <h3 className={styles.title}>
                {subsanacionMode
                  ? "Subsanar factura"
                  : invoiceType === "Pre-factura"
                  ? "Pre-factura"
                  : invoiceType === "Rectificativa" ||
                    invoiceType === "F. Rectificativa"
                  ? "Factura Rectificativa"
                  : "Factura"}
              </h3>
              {invoiceData?.numFactura && (
                <p className={styles.subtitle}>Nº {invoiceData.numFactura}</p>
              )}
            </div>
          </div>
          <button className={styles.closeButton} onClick={handleClose} disabled={submittingSubsanacion}>
            ✕
          </button>
        </div>

        {/* Content */}
        <div className={styles.content}>
          {loading ? (
            <div className={styles.loading}>
              <div className={styles.spinner}></div>
              <p>Cargando...</p>
            </div>
          ) : invoiceData ? (
            <div className={styles.formContainer}>
              {/* Información general */}
              <FormSection title="Información General">
                <div className={styles.row}>
                  <Input
                    label="Nº de Presupuesto"
                    value={
                      budgetData?.numPresupuesto
                        ? String(budgetData.numPresupuesto)
                        : "-"
                    }
                    readOnly
                    style={{ flex: 1 }}
                  />
                  <Input
                    label="Fecha"
                    value={formatDateToSpanish(invoiceData.fecha)}
                    readOnly
                    style={{ flex: 1 }}
                  />

                  {(invoiceType === "Factura" || invoiceType === "Rectificativa" || invoiceType === "F. Rectificativa") && (
                    <div style={{ flex: 1 }}>
                      {isEditingTipoCobro && !subsanacionMode ? (
                        <div style={{ display: 'flex', flexDirection: 'column', gap: '4px' }}>
                          <label style={{ fontSize: '12px', color: 'var(--dark-grey)', fontWeight: 500 }}>Tipo de Cobro</label>
                          <div style={{ display: 'flex', gap: '6px', alignItems: 'center' }}>
                            <select
                              className={styles.tipoCobroSelect}
                              value={editedTipoCobro?.value || ''}
                              onChange={(e) => setEditedTipoCobro(tipoCobroOptions.find(o => o.value === e.target.value) || null)}
                              style={{
                                flex: 1,
                                height: '38px',
                                border: '1px solid var(--main)',
                                borderRadius: '6px',
                                padding: '0 10px',
                                fontSize: '14px',
                                background: 'white',
                                color: '#111827',
                                WebkitTextFillColor: '#111827',
                                colorScheme: 'light',
                                cursor: 'pointer',
                                outline: 'none',
                              }}
                              autoFocus
                            >
                              <option value="" disabled style={{ color: '#111827', backgroundColor: '#ffffff' }}>Seleccionar...</option>
                              {tipoCobroOptions.map(opt => (
                                <option key={opt.value} value={opt.value} style={{ color: '#111827', backgroundColor: '#ffffff' }}>{opt.label}</option>
                              ))}
                            </select>
                            <button
                              onClick={handleSaveTipoCobro}
                              style={{ background: 'var(--green)', border: 'none', borderRadius: '4px', cursor: 'pointer', padding: '6px 10px', color: 'white', fontSize: '14px', height: '38px' }}
                              title="Guardar"
                            >✓</button>
                            <button
                              onClick={handleCancelEditTipoCobro}
                              style={{ background: 'var(--red)', border: 'none', borderRadius: '4px', cursor: 'pointer', padding: '6px 10px', color: 'white', fontSize: '14px', height: '38px' }}
                              title="Cancelar"
                            >✕</button>
                          </div>
                        </div>
                      ) : (
                        <Dropdown
                          label="Tipo de Cobro"
                          options={tipoCobroOptions}
                          selected={selectedTipoCobro}
                          disabled
                          style={{ marginTop: "3px", width: "100%" }}
                        />
                      )}
                    </div>
                  )}

                  {invoiceType === "Pre-factura" && (
                    <Dropdown
                      label="Tipo de Cobro"
                      options={tipoCobroOptions}
                      selected={selectedTipoCobro}
                      disabled
                      style={{ marginTop: "3px", width: "100%" }}
                      mainStyle={{ flex: 1 }}
                    />
                  )}
                </div>

                <div className={styles.row}>
                  <Input
                    label="IVA"
                    value={budgetData?.iva ? (String(budgetData.iva).endsWith('%') ? String(budgetData.iva) : `${budgetData.iva}%`) : "-"}
                    readOnly
                    style={{ flex: 1 }}
                  />
                  <Input
                    label="Coste Presupuesto"
                    value={`${invoiceData.costeTotal || 0}€`}
                    readOnly
                    style={{ flex: 1 }}
                  />

                  <Input
                    label="Porcentaje"
                    value={`${parseFloat(invoiceData.porcentage || 0).toFixed(2)}%`}
                    readOnly
                    style={{ flex: 1 }}
                  />
                  <Input
                    label="Coste Factura"
                    value={`${invoiceData.costeFinal || 0}€`}
                    readOnly
                    style={{ flex: 1 }}
                  />
                </div>

                {invoiceType === "Pre-factura" && (
                  <div className={styles.row}>
                    <Input
                      label="Estado Verifactu"
                      value={invoiceData.estadoVerifactu || "-"}
                      readOnly
                      style={{ flex: 1 }}
                    />
                  </div>
                )}
              </FormSection>

              {/* Información adicional para Factura */}
              {(invoiceType === "Factura" ||
                invoiceType === "Rectificativa" ||
                invoiceType === "F. Rectificativa") && (
                <FormSection
                  title="Estado y Pagos"
                  renderRight={() => (
                    <div className={styles.checkboxRow}>
                      <Checkbox
                        label="Cobrado"
                        checked={invoiceData.cobro || false}
                      />
                      <Checkbox
                        label="Honorarios"
                        checked={invoiceData.honorarios || false}
                      />
                    </div>
                  )}
                >
                  <div className={styles.row}>
                    <Input
                      label="Fecha de Cobro"
                      value={formatDateToSpanish(invoiceData.fechaCobro)}
                      readOnly
                      style={{ flex: 1 }}
                    />
                    <Input
                      label="Fecha de Honorarios"
                      value={formatDateToSpanish(invoiceData.fechaHonorarios)}
                      readOnly
                      style={{ flex: 1 }}
                    />
                  </div>
                  <div className={styles.row}>
                    <Input
                      label="Numero factura administrador"
                      value={invoiceData.numFacturaAdministrador || "-"}
                      readOnly
                      style={{ flex: 1 }}
                    />
                    <Input
                      label="Estado Verifactu"
                      value={invoiceData.estadoVerifactu || "-"}
                      readOnly
                      style={{ flex: 1 }}
                    />
                  </div>
                </FormSection>
              )}

              {isFacturaSubsanable(invoiceData.estadoVerifactu) && (invoiceData.motivoAeat || invoiceData.accionSiguiente) && (
                <FormSection title="Información de VeriFactu para la subsanación">
                  <div className={styles.row}>
                    <div className={styles.textareaContainer}>
                      <label className={styles.textareaLabel}>Motivo AEAT</label>
                      <textarea
                        className={styles.textarea}
                        value={invoiceData.motivoAeat || "-"}
                        readOnly
                        rows={4}
                      />
                    </div>
                    <div className={styles.textareaContainer}>
                      <label className={styles.textareaLabel}>Acción siguiente</label>
                      <textarea
                        className={styles.textarea}
                        value={invoiceData.accionSiguiente || "-"}
                        readOnly
                        rows={4}
                      />
                    </div>
                  </div>
                </FormSection>
              )}

              {/* Información de facturación */}
              <FormSection title="Datos de Facturación">
                <div className={styles.row}>
                  {subsanacionMode ? (
                    <Input
                      label="Nombre"
                      value={subsanacionForm.nombreFacturacion}
                      onChange={(e) => setSubsanacionForm(prev => ({ ...prev, nombreFacturacion: e.target.value }))}
                      style={{ flex: 1 }}
                    />
                  ) : (
                    <Input
                      label="Nombre"
                      value={invoiceData.nombreFacturacion || ""}
                      readOnly
                      style={{ flex: 1 }}
                    />
                  )}
                  {subsanacionMode ? (
                    <Input
                      label="Domicilio"
                      value={subsanacionForm.domicilioFacturacion}
                      onChange={(e) => setSubsanacionForm(prev => ({ ...prev, domicilioFacturacion: e.target.value }))}
                      style={{ flex: 1 }}
                    />
                  ) : (
                    <Input
                      label="Domicilio"
                      value={invoiceData.domicilioFacturacion || ""}
                      readOnly
                      style={{ flex: 1 }}
                    />
                  )}
                </div>

                <div className={styles.row}>
                  {subsanacionMode ? (
                    <Dropdown
                      label="Localidad"
                      options={localities}
                      selected={localities.find((loc) => loc.value === subsanacionForm.localidadFacturacionId) || null}
                      onSelect={(opt) => setSubsanacionForm(prev => ({ ...prev, localidadFacturacionId: opt?.value || null }))}
                      style={{ marginTop: "3px", width: "100%" }}
                      mainStyle={{ flex: 1 }}
                    />
                  ) : (
                    <Dropdown
                      label="Localidad"
                      options={localities}
                      selected={selectedLocality}
                      disabled
                      style={{ marginTop: "3px", width: "100%" }}
                      mainStyle={{ flex: 1 }}
                    />
                  )}
                  {subsanacionMode ? (
                    <Input
                      label="NIF"
                      value={subsanacionForm.nifFacturacion}
                      onChange={(e) => setSubsanacionForm(prev => ({ ...prev, nifFacturacion: e.target.value }))}
                      style={{ flex: 1 }}
                    />
                  ) : (
                    <Input
                      label="NIF"
                      value={invoiceData.nifFacturacion || ""}
                      readOnly
                      style={{ flex: 1 }}
                    />
                  )}
                  <Dropdown
                    label="Destinatario"
                    options={destinatarioOptions}
                    selected={selectedDestinatario}
                    disabled
                    style={{ marginTop: "3px", width: "100%" }}
                    mainStyle={{ flex: 1 }}
                  />
                </div>
              </FormSection>

              {/* Información adicional para Factura Rectificativa */}
              {(invoiceType === "Rectificativa" ||
                invoiceType === "F. Rectificativa") &&
                (invoiceData.numFacturaRectificada || invoiceData.motivo) && (
                  <FormSection title="Información de Rectificación">
                    {invoiceData.numFacturaRectificada && (
                    <div className={styles.row}>
                      <Input
                        label="Nº Factura que Rectifica"
                        value={invoiceData.numFacturaRectificada}
                        readOnly
                        style={{ flex: 1 }}
                      />
                      <Input
                        label="¿Factura original cobrada antes de rectificar?"
                        value={
                          invoiceData.cobradaAntesDe === true ? "Sí, estaba cobrada" :
                          invoiceData.cobradaAntesDe === false ? "No, no estaba cobrada" :
                          "-"
                        }
                        readOnly
                        style={{ flex: 1 }}
                      />
                    </div>
                    )}

                    {invoiceData.motivo && (
                      <div className={styles.row}>
                        <div className={styles.textareaContainer}>
                          <label className={styles.textareaLabel}>Motivo</label>
                          <textarea
                            className={styles.textarea}
                            value={invoiceData.motivo}
                            readOnly
                            rows={4}
                            spellCheck="true"
                          />
                        </div>
                      </div>
                    )}
                  </FormSection>
                )}

              {/* Líneas de Presupuesto */}
              {budgetData?.lineas && budgetData.lineas.length > 0 && (
                <Table
                  headers={[
                    { key: "gremio", label: "Gremio" },
                    { key: "presupuestoTotal", label: "Presupuesto Total" },
                    { key: "costeTotal", label: "Coste Total" },
                    { key: "conjuntoTotal", label: "R. Conjunto" },
                    { key: "beneficioTotal", label: "Beneficio" },
                    { key: "realizado", label: "Realizado" },
                    { key: "noRealizar", label: "No Realizar" },
                  ]}
                  data={budgetData.lineas
                    .filter(line => !line.noRealizar)
                    .map((line, index) => ({
                      id: index,
                      lineaId: line.id,
                      gremio: line.gremio,
                      presupuestoTotal: `${line.presupuestoTotal}€`,
                      costeTotal: `${line.costeTotal}€`,
                      conjuntoTotal: `${(line.conjuntoTotal * 100).toFixed(2)}%`,
                      beneficioTotal: `${parseFloat(line.beneficioTotal || 0).toFixed(2)}€`,
                      realizado: line.realizado ? "Sí" : "No",
                      noRealizar: line.noRealizar ? "Sí" : "No",
                    }))}
                  renderActions={(row) => (
                    <button
                      className={styles.editButton}
                      onClick={(e) => {
                        e.stopPropagation();
                        handleEditBudgetLine(row);
                      }}
                      onMouseDown={(e) => e.stopPropagation()}
                      title="Ver línea"
                    >
                      <MyIcons name="eye" width={20} height={20} color={"var(--main)"} />
                    </button>
                  )}
                />
              )}
            </div>
          ) : null}
        </div>

        {/* Footer */}
        <div className={styles.footer}>
          {invoiceType !== "Pre-factura" && !subsanacionMode && !isEditingTipoCobro && (
            <Button
              variant="secondary"
              onClick={handleStartEditTipoCobro}
              disabled={loading || !invoiceData}
              style={{ backgroundColor: "#f3f4f6", color: "#111827", borderColor: "#6b7280" }}
            >
              Editar tipo de cobro
            </Button>
          )}
          {subsanacionMode && (
            <Button
              variant="primary"
              onClick={handleSubmitSubsanacion}
              disabled={submittingSubsanacion || loading}
              style={{ backgroundColor: "#9a3412", color: "#ffffff" }}
            >
              {submittingSubsanacion ? "Enviando…" : "Enviar subsanación"}
            </Button>
          )}
          <Button variant="secondary" onClick={handleClose} disabled={submittingSubsanacion}>
            Cerrar
          </Button>
        </div>
      </div>

      {/* Budget Line Edit Modal */}
      {isBudgetLineModalOpen && selectedBudgetLineId && invoiceData?.expedienteId && (
        <BudgetLineEditModal
          isOpen={isBudgetLineModalOpen}
          onClose={handleBudgetLineModalClose}
          lineaId={selectedBudgetLineId}
          expedienteId={invoiceData.expedienteId}
        />
      )}
    </div>
  );
}
